/**
 * Main UI logic for sliding drawer to launch jobs.
 */
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {IconInfo} from '@wandb/weave/components/Icon';
import {useNodeValue} from '@wandb/weave/react';
import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {Loader} from 'semantic-ui-react';
import YAML from 'yaml';

import {
  RunQueueAccessType,
  useCreateDefaultResourceConfigMutation,
  useCreateRunQueueMutation,
  usePushToRunQueueMutation,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../../../pages/EntityLaunchPage';
import {Analytics} from '../../../services/analytics';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {queue as queueUrl} from '../../../util/urls';
import {CodeEditor} from '../CodeEditor';
import {useDestinationProjectOptions} from '../hooks';
import {getJobDefinitionFileOp, JobFile} from '../JobDetail/utilsJobFile';
import {JobDetails, JobRun, JobVersions} from '../JobDetail/utilTypes';
import {ProjectOption, SelectProject} from '../SelectProject2';
import {GroupedQueueOptions, QueueOption, SelectQueue} from '../SelectQueue2';
import {
  extractConfig,
  isWandbJobsProject,
  WANDB_ENTITY_NAME,
  WANDB_GLOBAL_CPU_QUEUE,
} from '../utils';
import {JsonObject, LaunchSpec} from '../utilTypes';
import * as S from './JobDrawerContentInner.styles';
import {JobDrawerOverrides} from './JobDrawerOverrides';

type JobDrawerContentInnerProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  job: JobDetails;
  runToClone?: string;
  customRunColors: Record<string, string>;
  queues: GroupedQueueOptions[];
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  onClose: () => void;
}>;

export const getDefaultCloneFrom = (
  runs: JobRun[],
  username: string,
  runToClone?: string
): JobRun | null => {
  if (runToClone) {
    for (const run of runs) {
      if (run.name === runToClone) {
        return run;
      }
    }
  }
  // Runs are sorted creation date descending, so the first run is the most recent.
  // TODO: Some debate over whether this should take job success into account.
  for (const run of runs) {
    if (run.creator?.username === username) {
      return run;
    }
  }
  if (runs.length > 0) {
    return runs[0];
  }
  return null;
};

const getDefaultQueue = (queues: GroupedQueueOptions[]): QueueOption | null => {
  if (queues.length === 0) {
    return null;
  }
  const group = queues[0];
  if (group.options.length === 0) {
    return null;
  }
  return group.options[0];
};

export const getVersionDescription = (
  versions: JobVersions,
  runTarget: string
) => {
  for (const version of versions) {
    if (version.aliases.some(alias => alias.alias === runTarget)) {
      return version.description;
    }
  }
  return '';
};

export const JobDrawerContentInner = ({
  entityName,
  projectName,
  job,
  runToClone,
  customRunColors,
  queues,
  finalizeCTA,
  children,
  overrideHintText,
  onFinalize,
  onClose,
}: JobDrawerContentInnerProps) => {
  const viewer = useViewer();
  const viewerEntity = viewer?.username ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which runs to show the user as clone options.
  const previousRuns = job.runs.filter(
    run => run.version === job.versionInfo.runTargetNum
  );
  const defaultCloneRun = getDefaultCloneFrom(
    previousRuns,
    viewer?.username ?? '',
    runToClone
  );
  const [cloneRun, setCloneRun] = useState<JobRun | null>(defaultCloneRun);
  // When user edits the overrides, clear any previous errors
  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const onOverridesChange = (value: string) => {
    setOverrides(value);
    setOverridesError('');
  };

  const defaultQueue = getDefaultQueue(queues);
  const [selectedQueue, setSelectedQueue] = useState<QueueOption | null>(
    defaultQueue
  );

  const setConfig = (config: JsonObject, args: string[]) => {
    const newOverrides = {
      args,
      run_config: config,
      entry_point: [],
    };
    const overrideStr = JSON.stringify(newOverrides, null, 4);
    onOverridesChange(overrideStr);
  };

  const contents = getJobDefinitionFileOp(
    entityName,
    projectName,
    job.name,
    job.versionInfo.runTarget ?? ''
  );
  const {result: jobDefinitionStr, loading} = useNodeValue(contents);

  const [showResourceConfig, setShowResourceConfig] = useState(false);
  const [resourceConfig, setResourceConfig] = useState('{}');
  const [resourceConfigError, setResourceConfigError] = useState('');
  const onResourceConfigChange = (value: string) => {
    setResourceConfig(value);
    setResourceConfigError('');
  };
  const [displayMode, setDisplayMode] = useState('json');
  const variantYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const variantJson = displayMode === 'json' ? 'secondary' : 'ghost';

  const setEditorType = (type: string) => {
    if (displayMode === type) {
      return;
    }
    // Convert the blob from previous format to current format
    if (type === 'json') {
      try {
        let parsed = YAML.parse(resourceConfig);
        if (!parsed) {
          parsed = {};
        }
        setResourceConfig(JSON.stringify(parsed, null, 2));
        setDisplayMode('json');
      } catch (e) {
        toast.error('Invalid YAML');
        return;
      }
    } else if (type === 'yaml') {
      try {
        let parsed = JSON.parse(resourceConfig);
        if (resourceConfig === '{}') {
          parsed = undefined; // show nothing for yaml when empty
        }
        setResourceConfig(YAML.stringify(parsed, {indent: 2}));
        setDisplayMode('yaml');
      } catch (e) {
        toast.error('Invalid JSON');
        return;
      }
    }
  };

  const [upsertProject] = useUpsertModelMutation();
  const [createDefaultResourceConfig] =
    useCreateDefaultResourceConfigMutation();
  const [createRunQueue] = useCreateRunQueueMutation();
  const [pushToRunQueue] = usePushToRunQueueMutation({
    context: propagateErrorsContext(),
  });

  const inWandbJobs = isWandbJobsProject(entityName, projectName);
  const [targetEntityName, setTargetEntityName] = useState(
    inWandbJobs ? '' : entityName
  );
  const [targetProjectName, setTargetProjectName] = useState(
    inWandbJobs ? '' : projectName
  );
  const onSelectProject = (option: ProjectOption) => {
    setTargetEntityName(option.entityName);
    setTargetProjectName(option.projectName);
  };
  const queryTargetProjects = useDestinationProjectOptions(
    entityName,
    projectName
  );

  const onSelectRun = (run: JobRun | null) => {
    if (run) {
      const config = extractConfig(run.config);
      const jobfile = new JobFile(jobDefinitionStr);
      const orderedConfig = jobfile.orderConfig(config);
      setConfig(orderedConfig, run.args);
      setCloneRun(run);
    } else {
      setCloneRun(null);
    }
  };
  useEffect(() => {
    if (!jobDefinitionStr || overrides) {
      // We need the job definition string for both defaults and ordering.
      return;
    }
    if (cloneRun) {
      onSelectRun(cloneRun);
    } else {
      const jobfile = new JobFile(jobDefinitionStr);
      setConfig(jobfile.getInputExample(), []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDefinitionStr, cloneRun]);

  if (loading || queryTargetProjects.loading) {
    return <Loader active />;
  }

  const onSelectQueue = (option: QueueOption) => {
    // Note: Intentionally not checking whether this is the currently selected option.
    // If it is, we reset the config, which seems useful.
    setSelectedQueue(option);
    setResourceConfig(
      displayMode === 'yaml'
        ? YAML.stringify(option.config, {indent: 2})
        : JSON.stringify(option.config, null, 2)
    );
  };

  const validate = (
    overridesToValidate: string,
    resourceConfigToValidate: string
  ): boolean => {
    let ok = true;
    try {
      JSON.parse(overridesToValidate);
    } catch (err) {
      setOverridesError('Overrides must be valid ');
      ok = false;
    }
    const rc = resourceConfigToValidate.trim() || '{}';
    try {
      if (displayMode === 'yaml') {
        YAML.parse(rc);
      } else {
        JSON.parse(rc);
      }
    } catch (err) {
      setResourceConfigError(
        'Resource config must be valid ' + displayMode.toUpperCase()
      );
      ok = false;
    }
    return ok;
  };

  const onLaunch = () => {
    if (onFinalize != null) {
      if (!validate(overrides, resourceConfig)) {
        return;
      }
      const rc = JSON.parse(resourceConfig);
      const resource = selectedQueue!.resourceType ?? 'local-container';
      let launchSpec = {
        entity: targetEntityName,
        project: targetProjectName,
        queue: selectedQueue!.queue,
        queueEntity: selectedQueue!.entity,
        job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
        overrides: JSON.parse(overrides),
      } as LaunchSpec;
      // if not empty, add resource_args
      if (Object.keys(rc).length > 0) {
        launchSpec = {...launchSpec, resource_args: {[resource]: rc}};
      }
      onFinalize(launchSpec);
      return;
    }
    // actually pushing job to queue
    setIsSubmitting(true);
    if (validate(overrides, resourceConfig)) {
      pushRunToQueue();
      Analytics.track('clicked launch now', {
        entityName,
        projectName,
        resourceConfig,
        location: 'job drawer',
      });
    } else {
      setIsSubmitting(false);
    }
  };

  const pushRunToQueue = async () => {
    if (!selectedQueue) {
      setIsSubmitting(false);
      return;
    }
    let queueName = selectedQueue.queue;
    // creationEntity is the entity a new queue would be created in.
    const creationEntity =
      entityName === WANDB_ENTITY_NAME ? viewerEntity : entityName;
    const queueEntity = selectedQueue.entity || creationEntity;
    let queueID = selectedQueue.queueID;
    if (selectedQueue.queue === '') {
      // Create a starter queue
      // First, ensure we have the necessary project for an entity-scoped queue.
      const createProjectResponse = await upsertProject({
        variables: {
          entityName: creationEntity,
          name: DEFAULT_LAUNCH_PROJECT_NAME,
        },
      });
      if (createProjectResponse.errors != null) {
        toast('Error creating entity queue project.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'upsertProject',
          errors: createProjectResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }

      const completeConfig = {
        resource_args: {
          'local-container': {},
        },
      };
      const createConfigResponse = await createDefaultResourceConfig({
        variables: {
          entityName: creationEntity,
          resource: 'local-container',
          config: JSON.stringify(completeConfig),
        },
      });
      if (createConfigResponse.errors != null) {
        toast('Error creating entity queue config.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createDefaultResourceConfig',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      const defaultResourceConfigID =
        createConfigResponse.data?.createDefaultResourceConfig
          ?.defaultResourceConfigID;

      queueName = 'Starter queue';
      const createQueueResponse = await createRunQueue({
        variables: {
          entityName: creationEntity,
          projectName: DEFAULT_LAUNCH_PROJECT_NAME,
          queueName,
          access: RunQueueAccessType.Project,
          defaultResourceConfigID,
        },
      });
      if (createQueueResponse.errors != null) {
        toast('Error creating entity queue.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createRunQueue',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      Analytics.track('create run queue success', {
        entityName: creationEntity,
        queueName,
        location: 'job drawer',
      });
      queueID = createQueueResponse.data?.createRunQueue?.queueID ?? '';
    }

    const config: JsonObject = {
      entity: targetEntityName,
      project: targetProjectName,
      job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
      overrides: JSON.parse(overrides),
    };
    if (selectedQueue.resourceType) {
      config.resource_args = {
        [selectedQueue.resourceType]:
          displayMode === 'json'
            ? JSON.parse(resourceConfig)
            : YAML.parse(resourceConfig),
      };
    }
    const pushedRunSpec = JSON.stringify(config);
    try {
      const pushResp = await pushToRunQueue({
        variables: {
          queueID,
          runSpec: pushedRunSpec,
        },
      });
      const newItemId = pushResp.data?.pushToRunQueue?.runQueueItemId;
      if (newItemId) {
        toast.success(
          <S.SuccessfulQueue>
            <IconInfo width={48} height={48} />
            <S.SuccessfulQueueMessage>
              Your job was successfully added to '{queueName}'.{' '}
              <TargetBlank href={queueUrl(queueEntity, queueID, 'runs')}>
                View queue
              </TargetBlank>
            </S.SuccessfulQueueMessage>
          </S.SuccessfulQueue>
        );
        setIsSubmitting(false);
      } else {
        throw new Error();
      }
    } catch (err) {
      const errorMsg: string =
        (err as any)?.networkError?.result?.errors?.[0]?.message ??
        'Something went wrong while trying to queue this job.';
      toast.error(errorMsg);
      setIsSubmitting(false);
      return;
    }
    onClose();
  };

  // Don't show resource configuration if creating a starter queue or using Global Queue.
  // TODO: Reserve our global queue name?
  const hasResourceConfig =
    selectedQueue &&
    selectedQueue.resourceType &&
    selectedQueue.queue !== '' &&
    selectedQueue.queue !== WANDB_GLOBAL_CPU_QUEUE;

  return (
    <>
      <S.JobDrawerInnerMain>
        <JobDrawerOverrides
          overrides={overrides}
          onOverridesChange={onOverridesChange}
          previousRuns={previousRuns}
          cloneRun={cloneRun}
          onSelectRun={onSelectRun}
          customRunColors={customRunColors}
        />
        {overrideHintText && <S.HintText>{overrideHintText}</S.HintText>}
        {overridesError && <S.Error>{overridesError}</S.Error>}
        <S.LabelRow>
          <S.Label>Queue</S.Label>
        </S.LabelRow>
        <SelectQueue
          options={queues}
          defaultQueueOption={defaultQueue ?? undefined}
          onSelectQueue={onSelectQueue}
        />
        <S.HintText>Inactive queues are unavailable for selection.</S.HintText>
        {hasResourceConfig && (
          <>
            <S.LabelRow>
              <S.Label>Resource config</S.Label>
              <S.ToggleConfig>
                {showResourceConfig && (
                  <Button
                    variant={variantYaml}
                    onClick={() => setEditorType('yaml')}>
                    YAML
                  </Button>
                )}
                {showResourceConfig && (
                  <Button
                    variant={variantJson}
                    onClick={() => setEditorType('json')}>
                    JSON
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowResourceConfig(!showResourceConfig);
                  }}>
                  {showResourceConfig ? 'Hide' : 'Show'}
                </Button>
              </S.ToggleConfig>
            </S.LabelRow>
            {showResourceConfig && (
              <CodeEditor
                value={resourceConfig}
                language={displayMode}
                onChange={onResourceConfigChange}
              />
            )}
            {resourceConfigError && <S.Error>{resourceConfigError}</S.Error>}
          </>
        )}
        <S.LabelRow>
          <S.Label>Destination project</S.Label>
        </S.LabelRow>
        <SelectProject
          defaultEntityName={targetEntityName}
          defaultProjectName={targetProjectName}
          options={queryTargetProjects.options}
          onSelectProject={onSelectProject}
        />
        {children}
      </S.JobDrawerInnerMain>
      <Button
        className="w-full"
        variant="primary"
        size="large"
        onClick={onLaunch}
        disabled={
          !selectedQueue ||
          !targetEntityName ||
          !targetProjectName ||
          isSubmitting
        }>
        {finalizeCTA ?? 'Launch now'}
      </Button>
    </>
  );
};
